import { useEffect, useMemo, useState } from 'react'
import { X, Loader2, Trash2, CalendarDays, UserRound, StickyNote, Building2, AlertTriangle } from 'lucide-react'
import { postJson, apiDelete } from '../../lib/api.js'
import { relabelBranch, fullName, toDateInput } from '../../lib/employee.js'
import { LEAVE_TYPES, leaveTypeMeta, LEAVE_SICK, LEAVE_UNPAID } from '../../lib/timetable.js'
import { blankLeave, leaveError } from '../../lib/leave.js'
import EmployeePicker from './EmployeePicker.jsx'
import ConfirmDialog from '../ConfirmDialog.jsx'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10 disabled:bg-slate-50 disabled:text-slate-400'
const LABEL = 'block text-[12.5px] font-medium text-slate-500 mb-1'

function SectionHead({ icon: Icon, title, note }) {
  return (
    <div className="flex items-center gap-2 mt-6 mb-2.5">
      <Icon size={15} className="text-brand-600" strokeWidth={1.9} />
      <h3 className="text-[13.5px] font-semibold text-slate-700">{title}</h3>
      {note && <span className="text-[11.5px] text-slate-400">{note}</span>}
    </div>
  )
}

/**
 * Drawer to add or edit a single leave day (employee_timetable ลากิจ/ลาป่วย row).
 *
 *   row       existing leave row → edit mode (employee is fixed); null → add.
 *   defaults  seed for add mode ({ employee_id, branch_id, date, type }).
 *   branches  [{id,name}] for the branch <select>.
 *   employees roster (id→branch_id) so picking a person auto-fills their branch.
 */
export default function LeaveEditor({ row, defaults, branches = [], employees = [], onClose, onSaved }) {
  const editing = !!row?.id
  const [form, setForm] = useState(() => (
    editing
      ? {
          id: row.id,
          employee_id: String(row.employee_id ?? ''),
          branch_id: String(row.branch_id ?? ''),
          type: LEAVE_TYPES.includes(row.type) ? row.type : LEAVE_TYPES[0],
          date: toDateInput(row.date),
          unnotice: row.unnotice === true,
          med_certificate: row.med_certificate === true,
          remark: row.remark || '',
        }
      : blankLeave({
          employee_id: defaults?.employee_id ? String(defaults.employee_id) : '',
          branch_id: defaults?.branch_id ? String(defaults.branch_id) : '',
          date: defaults?.date || '',
          type: defaults?.type || LEAVE_TYPES[0],
        })
  ))
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [confirmDel, setConfirmDel] = useState(false)

  const set = (patch) => setForm((f) => ({ ...f, ...patch }))

  const empById = useMemo(() => {
    const m = new Map()
    for (const e of employees) m.set(String(e.id), e)
    return m
  }, [employees])

  // Picking an employee (add mode) fills the branch with their working branch,
  // unless the user already chose one.
  function onPickEmployee(id) {
    const e = empById.get(String(id))
    setForm((f) => ({
      ...f,
      employee_id: id,
      branch_id: f.branch_id || (e?.branch_id != null ? String(e.branch_id) : ''),
    }))
  }

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !confirmDel) onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, confirmDel])

  const editEmp = editing ? empById.get(String(row.employee_id)) : null
  const editCode = editing ? (editEmp?.code || row?.employee?.code || `#${row.employee_id}`) : ''
  const editName = editEmp ? fullName(editEmp) : ''

  async function save() {
    if (!form.employee_id) { setErr('กรุณาเลือกพนักงาน'); return }
    if (!form.date) { setErr('กรุณาระบุวันที่ลา'); return }
    setSaving(true); setErr('')
    try {
      await postJson('/api/hr/leave', {
        id: form.id || undefined,
        employee_id: Number(form.employee_id),
        branch_id: form.branch_id ? Number(form.branch_id) : 0,
        type: form.type,
        date: form.date,
        unnotice: !!form.unnotice,
        med_certificate: !!form.med_certificate,
        remark: form.remark.trim(),
      })
      onSaved?.()
    } catch (e) { setErr(leaveError(e)) }
    finally { setSaving(false) }
  }

  async function remove() {
    setConfirmDel(false)
    setSaving(true); setErr('')
    try {
      await apiDelete(`/api/hr/leave?id=${row.id}&employee_id=${Number(row.employee_id) || 0}`)
      onSaved?.()
    } catch (e) { setErr(leaveError(e)) }
    finally { setSaving(false) }
  }

  const sickSelected = form.type === LEAVE_SICK

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-[520px] bg-white shadow-2xl flex flex-col animate-ss-slide-in">
        {/* header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="min-w-0">
            <h2 className="text-[16px] font-semibold text-slate-900 truncate">
              {editing ? 'แก้ไขวันลา' : 'เพิ่มวันลา'}
            </h2>
            <p className="text-[12.5px] text-slate-500">{editing ? (editName ? `${editCode} · ${editName}` : editCode) : 'บันทึกวันลาของพนักงาน'}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 shrink-0"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* employee */}
          <SectionHead icon={UserRound} title="พนักงาน" note={editing ? 'เปลี่ยนพนักงานไม่ได้' : undefined} />
          {editing ? (
            <div className={`${FIELD} bg-slate-50`}>
              <span className="font-semibold text-slate-700 tabular-nums">{editCode}</span>
              {editName && <span className="text-slate-400"> · {editName}</span>}
            </div>
          ) : (
            <EmployeePicker value={form.employee_id} onChange={onPickEmployee} className="w-full" />
          )}

          {/* date + type */}
          <SectionHead icon={CalendarDays} title="วันและประเภทการลา" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>วันที่ลา</label>
              <input type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} className={FIELD} />
            </div>
            {/* Full row: three types do not fit half a 520px drawer. */}
            <div className="col-span-2">
              <label className={LABEL}>ประเภท</label>
              <div className="flex gap-2">
                {LEAVE_TYPES.map((t) => {
                  const meta = leaveTypeMeta(t)
                  const on = form.type === t
                  return (
                    <button key={t} type="button" onClick={() => set({ type: t })}
                      className={`flex-1 rounded-xl px-3 py-2 text-[13px] font-medium ring-1 transition ${
                        on ? meta.tone : 'bg-white text-slate-500 ring-slate-200 hover:bg-slate-50'}`}>
                      {meta.label}
                    </button>
                  )
                })}
              </div>
              {form.type === LEAVE_UNPAID && (
                <p className="text-[11.5px] text-slate-500 mt-1.5">ไม่นับในโควตาลากิจ · พนักงานไม่ได้รับเงินเดือนวันนั้น และไม่ได้เบี้ยขยันรอบนั้น</p>
              )}
            </div>
          </div>

          {/* branch */}
          <SectionHead icon={Building2} title="สาขา" />
          <select value={form.branch_id} onChange={(e) => set({ branch_id: e.target.value })} className={FIELD}>
            <option value="">— ไม่ระบุสาขา —</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{relabelBranch(b.name)}</option>)}
          </select>

          {/* flags */}
          <SectionHead icon={AlertTriangle} title="รายละเอียดการลา" />
          <div className="space-y-2">
            <button type="button" onClick={() => set({ unnotice: !form.unnotice })}
              className={`w-full inline-flex items-center gap-2 rounded-xl px-3 py-2.5 text-[13px] font-medium ring-1 transition ${
                form.unnotice ? 'bg-rose-50 text-rose-700 ring-rose-200' : 'bg-slate-50 text-slate-500 ring-slate-200'}`}>
              <span className={`h-2 w-2 rounded-full ${form.unnotice ? 'bg-rose-500' : 'bg-slate-300'}`} />
              ลาไม่แจ้งล่วงหน้า
            </button>
            <button type="button" onClick={() => set({ med_certificate: !form.med_certificate })}
              className={`w-full inline-flex items-center gap-2 rounded-xl px-3 py-2.5 text-[13px] font-medium ring-1 transition ${
                form.med_certificate ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-slate-50 text-slate-500 ring-slate-200'}`}>
              <span className={`h-2 w-2 rounded-full ${form.med_certificate ? 'bg-emerald-500' : 'bg-slate-300'}`} />
              มีใบรับรองแพทย์
              {sickSelected && !form.med_certificate && <span className="text-[11px] text-slate-400 font-normal ml-auto">แนะนำสำหรับลาป่วย</span>}
            </button>
          </div>

          {/* remark */}
          <SectionHead icon={StickyNote} title="หมายเหตุ" />
          <textarea rows={3} value={form.remark} onChange={(e) => set({ remark: e.target.value })}
            placeholder="เหตุผลหรือรายละเอียดเพิ่มเติม…" className={FIELD} />

          {!editing && (
            <p className="text-[11.5px] text-slate-400 mt-3">
              เพิ่มวันลาไม่ได้ถ้าพนักงานมีตารางเวรหรือวันลาในวันนั้นอยู่แล้ว
            </p>
          )}
        </div>

        {/* footer */}
        <div className="border-t border-slate-100 px-6 py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {editing && (
              <button onClick={() => setConfirmDel(true)} disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50">
                <Trash2 size={14} /> ลบวันลา
              </button>
            )}
            <span className="text-[12.5px] text-rose-500 truncate">{err}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={onClose} className="px-4 py-2 rounded-xl text-[13.5px] font-medium text-slate-600 hover:bg-slate-100">ปิด</button>
            <button onClick={save} disabled={saving}
              className="px-4 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 inline-flex items-center gap-1.5">
              {saving && <Loader2 size={14} className="animate-spin" />} บันทึก
            </button>
          </div>
        </div>
      </div>

      {confirmDel && (
        <ConfirmDialog
          title="ลบวันลา"
          message="ลบรายการวันลานี้ออกจากระบบ"
          confirmLabel="ลบวันลา"
          tone="danger"
          onConfirm={remove}
          onCancel={() => setConfirmDel(false)}
        />
      )}
    </div>
  )
}
